import { useEffect, useRef, useState } from "react";
import { magicBackBG } from "../../../../appAssets";
import useRemintAsset from "../../../../../hooks/useRemintAsset";
import { isAssetValid, AUDIO_BUFFER_SEC } from "../../../../../utils/assetExpiry";
import "./RenderAudioViewer.scss";

interface RenderAudioViewerProps {
  audioPathUrl: string;
  displayQuestion?: string;
  description?: string;
  onClose: () => void;
}

function formatTime(sec: number): string {
  if (!Number.isFinite(sec)) return "0:00";
  const m = Math.floor(sec / 60);
  const s = Math.floor(sec % 60);
  return `${m}:${s.toString().padStart(2, "0")}`;
}

/**
 * Mirrors mobile RenderAuidoViewer behaviour:
 *  - shows description + question
 *  - plays the audio with play/pause + scrub progress
 */
function RenderAudioViewer({ audioPathUrl, displayQuestion, description, onClose }: RenderAudioViewerProps) {
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [progress, setProgress] = useState(0);
  const [duration, setDuration] = useState(0);
  const [activeUrl, setActiveUrl] = useState(audioPathUrl);
  const { remint } = useRemintAsset();

  // On URL change: check expiry and remint if the token is about to expire.
  useEffect(() => {
    if (!audioPathUrl) { setActiveUrl(audioPathUrl); return; }
    if (isAssetValid(audioPathUrl, AUDIO_BUFFER_SEC)) {
      setActiveUrl(audioPathUrl);
    } else {
      remint([audioPathUrl]).then(([fresh]) => setActiveUrl(fresh ?? audioPathUrl));
    }
  }, [audioPathUrl]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!activeUrl) return;
    const audio = new Audio(activeUrl);
    audioRef.current = audio;

    const onTime = () => setProgress(audio.currentTime);
    const onMeta = () => setDuration(audio.duration || 0);
    const onEnded = () => setIsPlaying(false);

    audio.addEventListener("timeupdate", onTime);
    audio.addEventListener("loadedmetadata", onMeta);
    audio.addEventListener("ended", onEnded);

    audio.play().then(() => setIsPlaying(true)).catch(() => setIsPlaying(false));

    return () => {
      audio.pause();
      audio.removeEventListener("timeupdate", onTime);
      audio.removeEventListener("loadedmetadata", onMeta);
      audio.removeEventListener("ended", onEnded);
      audioRef.current = null;
    };
  }, [activeUrl]); // keyed on activeUrl so a reminted URL recreates the Audio element

  const togglePlay = async () => {
    const a = audioRef.current;
    if (!a) return;
    if (isPlaying) {
      a.pause();
      setIsPlaying(false);
    } else {
      // Check if token still covers remaining playback duration + buffer
      const remaining = (a.duration || 0) - a.currentTime;
      if (!isAssetValid(activeUrl, remaining + AUDIO_BUFFER_SEC)) {
        const [fresh] = await remint([activeUrl]);
        if (fresh && fresh !== activeUrl) {
          setActiveUrl(fresh); // triggers audio recreation via useEffect above
          return;
        }
      }
      a.play().then(() => setIsPlaying(true)).catch(() => {});
    }
  };

  const handleSeek = (e: React.ChangeEvent<HTMLInputElement>) => {
    const a = audioRef.current;
    if (!a) return;
    const next = parseFloat(e.target.value);
    a.currentTime = next;
    setProgress(next);
  };

  return (
    <div className="audio-viewer" style={{ backgroundImage: `url(${magicBackBG})` }}>
      <div className="audio-viewer__header">
        <div />
        <button
          type="button"
          className="audio-viewer__close"
          onClick={onClose}
          aria-label="Close"
        >
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round">
            <line x1="18" y1="6" x2="6" y2="18" />
            <line x1="6" y1="6" x2="18" y2="18" />
          </svg>
        </button>
      </div>

      <div className="audio-viewer__body">
        {displayQuestion && <h2 className="audio-viewer__title">{displayQuestion}</h2>}
        {description && <p className="audio-viewer__desc">{description}</p>}

        <button
          type="button"
          className="audio-viewer__play-btn"
          onClick={togglePlay}
          aria-label={isPlaying ? "Pause" : "Play"}
        >
          {isPlaying ? (
            <svg width="44" height="44" viewBox="0 0 24 24" fill="currentColor">
              <rect x="6" y="5" width="4" height="14" rx="1" />
              <rect x="14" y="5" width="4" height="14" rx="1" />
            </svg>
          ) : (
            <svg width="44" height="44" viewBox="0 0 24 24" fill="currentColor">
              <path d="M8 5v14l11-7z" />
            </svg>
          )}
        </button>

        <div className="audio-viewer__progress">
          <span className="audio-viewer__time">{formatTime(progress)}</span>
          <input
            type="range"
            min={0}
            max={duration || 1}
            step={0.1}
            value={progress}
            onChange={handleSeek}
            className="audio-viewer__scrub"
          />
          <span className="audio-viewer__time">{formatTime(duration)}</span>
        </div>
      </div>
    </div>
  );
}

export default RenderAudioViewer;
